import ProductListingLevels from 'components/Constants/productListingLevels'
import { ListingMatch } from 'components/Models/listingSearch'
import { ListingLevel } from 'components/Models/product'
import { ProductTypePropertiesInputType } from 'components/Models/productType'
import { SearchFacetInput } from 'components/Models/searchFacetInput'
import {
    PartnershipsOption,
    StatusOption,
} from './Filter/MoreFilterSubCategories'
import { ListingStatus } from './ListingStatusVariant'
import { NormalizedProductType } from './model'
import {
    queryNameToDateParamMap,
    queryNameToFacetNameMap,
} from './queryNameMaps'

export const defaultSortAndGroup =
    'groupby=sectionNumber false,productType false&sortby=sectionNumber,productType,type,lastUpdated desc&defaultFilter=true'

export const removeFamilyNumberFromFamilyNameRegex = new RegExp(
    '(\\d+(\\.\\d+)*\\s-\\s)',
    'g'
)

// A.K.A Family Name
//value = section name and product type
export const getSectionName = (value?: string) =>
    value ? value.split('%|%')?.[0]?.slice(1, -1) : ''

//value = section name and product type
export const getProductTypeName = (value?: string) =>
    value ? value.split('%|%')?.[1]?.slice(1, -1) : ''

export const getFacetInputsFromFilterSelection = (
    filterSelectionData: Record<string, string[]> | undefined
): SearchFacetInput[] => {
    let facets: SearchFacetInput[] = []
    const filterSelection: Record<string, string[]> | undefined = {
        ...filterSelectionData,
    }
    for (let facetName in filterSelection) {
        let facetValues: string[] = filterSelection[facetName]
        if (
            facetValues &&
            facetValues.length &&
            facetName !== 'additionalContent' &&
            facetName !== 'orderBy'
        ) {
            facets = [
                ...facets,
                {
                    name: facetName,
                    values: [...facetValues],
                },
            ]
        }
    }
    return facets
}

export const escapeElementDelimiter = (plaintext: string): string => {
    return plaintext.replace(/,/gi, '\\u002c')
}
export const unescapeElementDelimiter = (escapetext: string): string => {
    return escapetext.replace(/\\u002c/gi, ',')
}

export const updateQueryString = (
    filterSelection: Record<string, string[]> | undefined,
    inputSelection: Record<string, Date | string | null> | undefined,
    history: any,
    location: any,
    //this prop is temporary during ae-bpm merge and will be removed once BPM will be deprcated
    callingFromAE?: boolean
): void => {
    let newQS: string = ''
    let queryParams = new URLSearchParams(location.search)
    const mergedClientURL = callingFromAE ? '/bpm' : ''
    if (filterSelection) {
        for (let index in queryNameToFacetNameMap) {
            let vals = filterSelection[
                queryNameToFacetNameMap[index].facetName
            ]?.map((v) => escapeElementDelimiter(v))

            queryParams.set(
                queryNameToFacetNameMap[index].queryName,
                vals?.join(',')
            )
        }
    }

    if (inputSelection) {
        for (let index in queryNameToDateParamMap) {
            let val = inputSelection[
                queryNameToDateParamMap[index].facetName
            ]?.toString()
            queryParams.set(queryNameToDateParamMap[index].queryName, val ?? '')
        }
    }

    // Filter undefined values from querystring
    queryParams.forEach(function (value, key) {
        if (value && value !== 'undefined') {
            newQS += `${key}=${encodeURIComponent(value)}&`
        }
    })

    // Cleanup
    if (newQS.endsWith('&')) {
        newQS = newQS.slice(0, -1)
    }

    history.push(
        `${mergedClientURL}/products/all${!newQS.length ? '' : '?'}${newQS}`
    )
}

export const getSelectedValuesFromQueryString = (
    querystring: string
): Record<string, string[]> => {
    let selectedValues: Record<string, string[]> = {}

    // Setting isActive default to true for now for EA.
    querystring = querystring.length > 0 ? querystring : 'ia=true'

    let queryParams = new URLSearchParams(querystring)

    for (let index in queryNameToFacetNameMap) {
        let vals = queryParams
            .get(queryNameToFacetNameMap[index].queryName)
            ?.split(',')

        if (vals && vals.length > 0) {
            selectedValues[
                queryNameToFacetNameMap[index].facetName
            ] = vals.map((v) => unescapeElementDelimiter(v))
        }
    }
    return selectedValues
}

export const getInputSelectionValuesFromQueryString = (
    querystring: string
): Record<string, Date | string | null> => {
    let inputSelectionValues: Record<string, Date | string | null> = {}
    let queryParams = new URLSearchParams(querystring)

    for (let index in queryNameToDateParamMap) {
        let val = queryParams.get(queryNameToDateParamMap[index].queryName)
        if (val) {
            inputSelectionValues[
                queryNameToDateParamMap[index].facetName
            ] = decodeURIComponent(unescapeElementDelimiter(val))
        }
    }
    return inputSelectionValues
}

export const getSelectedProductTypesFromQueryString = (
    querystring: string
): NormalizedProductType[] => {
    let selectedPTs: NormalizedProductType[] = []
    let queryParams = new URLSearchParams(querystring)
    let vals = queryParams.get('sp')?.split(',') //sectionNameWithProductType
    let ptpString = decodeURIComponent(
        (queryParams.has('ptp') && queryParams.get('ptp')) || ''
    ) //productTypeproperties
    let productTypeproperties: Record<
        string,
        ProductTypePropertiesInputType[]
    > = {}
    if (ptpString) {
        ptpString.split(';')?.forEach((ptp) => {
            const [typeName, catWithValues] = ptp?.split('%|%')
            const [category, values] = catWithValues?.split(':')
            productTypeproperties[typeName] = productTypeproperties[typeName]
                ? [
                      ...productTypeproperties[typeName],
                      {
                          propertyName: category,
                          propertyOptionIds: values?.split(','),
                      },
                  ]
                : [
                      {
                          propertyName: category,
                          propertyOptionIds: values?.split(','),
                      },
                  ]
        })
    }
    if (vals) {
        selectedPTs = vals.map((v) => {
            const unescapedVal = unescapeElementDelimiter(v)
            return {
                name: unescapedVal,
                value: unescapedVal,
                sectionNameWithoutNmber: getSectionName(unescapedVal)?.replace(
                    removeFamilyNumberFromFamilyNameRegex,
                    ''
                ),
                sectionName: getSectionName(unescapedVal),
                productTypeName: getProductTypeName(unescapedVal),
                productTypeProperties: (productTypeproperties[
                    getProductTypeName(unescapedVal)
                ] ?? []) as ProductTypePropertiesInputType[],
            }
        })
    }

    return selectedPTs
}

export const isAdditionalContentSelected = (
    additionalContentFilter: string[],
    catValue: string
): boolean | undefined => {
    let isSelected: boolean = false
    if (additionalContentFilter && additionalContentFilter.length) {
        isSelected = additionalContentFilter.some(
            (category: string) => category === catValue
        )
    }
    if (isSelected) return true
    return undefined
}

export const createFilter = (
    facets: SearchFacetInput[],
    searchType: 'Search' | 'AutoSuggest',
    facetName?: string,
    excludeListingLevels?: string[],
    onlyActive?: boolean
): SearchFacetInput[] => {
    const facetNames = facets.map((each) => each.name)

    const withManufacturerOption = facetNames.some(
        (each) => each === 'withManufacturerOption'
    )
    const manufacturerOptionsDefaultLevels: string[] = [
        ListingLevel.Premium,
        ListingLevel.PremiumMasterSpec,
    ]
    const manufacturerOptionsExcludedLevels: string[] = [
        ListingLevel.PremiumProprietary,
        ListingLevel.Manufacturer,
    ]

    let filteredFacets = facets.filter(
        (each) => each.name !== 'sectionNameWithProductType'
    )

    if (onlyActive) {
        filteredFacets = [
            {
                name: 'isActive',
                values: ['true'],
            },
            ...filteredFacets.filter(
                (eachValue) => eachValue.name !== 'isActive'
            ),
        ]
    }

    if (
        searchType === 'AutoSuggest' &&
        facetName === 'sectionNameWithProductType'
    ) {
        {
            const productTypeSectionNames = facets
                .filter(
                    (each) => each.name === 'sectionNameWithProductType'
                )?.[0]
                ?.values.map((each) => getSectionName(each))

            if (productTypeSectionNames?.length > 0) {
                const hasSectionNames = filteredFacets.some(
                    (each) => each.name === 'sectionNames'
                )
                if (hasSectionNames) {
                    return filteredFacets.map((each) => {
                        if (each.name === 'sectionNames') {
                            return {
                                ...each,
                                values: Array.from(
                                    new Set([
                                        ...each.values,
                                        ...(productTypeSectionNames ?? []),
                                    ])
                                ),
                            }
                        }
                        return each
                    })
                }
                return [
                    {
                        name: 'sectionNames',
                        values: productTypeSectionNames,
                    },
                    ...filteredFacets,
                ]
            }
        }
    }

    if (
        facetName === 'sectionNames' ||
        facetName === 'sectionNameWithProductType'
    ) {
        return filteredFacets
            .map((facet) => {
                if (facet.name === 'listingLevels') {
                    let listingLevelValues = facet.values
                    if (excludeListingLevels) {
                        listingLevelValues = listingLevelValues.filter(
                            (each) => !excludeListingLevels?.includes(each)
                        )
                    }
                    if (withManufacturerOption) {
                        listingLevelValues = listingLevelValues.filter(
                            (value) =>
                                !manufacturerOptionsExcludedLevels.includes(
                                    value
                                )
                        )
                    }
                    return { ...facet, values: listingLevelValues }
                }
                return facet
            })
            .filter((each) => each.name !== 'withManufacturerOption')
    } else {
        filteredFacets = filteredFacets.map((facet) => {
            if (facet.name === 'listingLevels') {
                let validListingLevels = facet.values
                if (excludeListingLevels) {
                    validListingLevels = validListingLevels.filter(
                        (each) => !excludeListingLevels?.includes(each)
                    )
                }

                if (withManufacturerOption) {
                    validListingLevels =
                        validListingLevels.filter(
                            (value) =>
                                !manufacturerOptionsExcludedLevels.includes(
                                    value
                                )
                        ) || []

                    return {
                        ...facet,
                        values:
                            validListingLevels.length === 0
                                ? manufacturerOptionsDefaultLevels
                                : validListingLevels,
                    }
                }
            }
            return facet
        })

        const withManufacturerOptionOnly =
            facetNames.includes('withManufacturerOption') &&
            !facetNames.includes('listingLevels')

        if (withManufacturerOptionOnly) {
            filteredFacets.push({
                name: 'listingLevels',
                values: manufacturerOptionsDefaultLevels,
            })
        }

        const excludedListingLevelsOnly =
            excludeListingLevels &&
            !facetNames.includes('withManufacturerOption') &&
            !facetNames.includes('listingLevels')
        if (excludedListingLevelsOnly) {
            filteredFacets.push({
                name: 'listingLevels',
                values: ProductListingLevels.filter(
                    (each) => !excludeListingLevels?.includes(each)
                ),
            })
        }
    }

    return filteredFacets
}

export const getFilterDisabledValues = (
    filterFacets: Record<string, string[]>,
    hasManufacturerOptions: boolean
): Record<string, string[]> => {
    const resultListingLevels = filterFacets.ListingLevels ?? []
    const resultPartnership = filterFacets.Partnerships ?? []
    const resultSubmissionStatus = filterFacets.SubmissionStatus ?? []
    const resultStatus = filterFacets.IsActive ?? []
    const addlCon: Record<string, boolean> = {
        hasBimLinks: filterFacets?.HasBimLinks?.map((each) =>
            each?.toLowerCase()
        ).includes('true'),
        hasCatalogs: filterFacets?.HasCatalogs?.map((each) =>
            each?.toLowerCase()
        ).includes('true'),
        hasDocumentFL: filterFacets?.HasDocumentFL?.map((each) =>
            each?.toLowerCase()
        ).includes('true'),
        hasDocumentSF: filterFacets?.HasDocumentSF?.map((each) =>
            each?.toLowerCase()
        ).includes('true'),
    }

    const disabledListingLevels = ProductListingLevels.filter(
        (level) => resultListingLevels && !resultListingLevels.includes(level)
    )

    return {
        listingLevels: hasManufacturerOptions
            ? [
                  ...disabledListingLevels,
                  ListingLevel.Manufacturer,
                  ListingLevel.PremiumProprietary,
              ]
            : disabledListingLevels,
        partnerships: Object.values(PartnershipsOption).filter(
            (partnership) =>
                resultPartnership && !resultPartnership.includes(partnership)
        ),
        submissionStatus: Object.values(ListingStatus).filter(
            (state) =>
                resultSubmissionStatus &&
                !resultSubmissionStatus.includes(state)
        ),
        status: Object.keys(StatusOption).filter(
            (statusOption) =>
                resultStatus &&
                !resultStatus.includes(
                    statusOption.substr(0, 1).toUpperCase() +
                        statusOption.substr(1)
                )
        ),
        additionalContent: Object.keys(addlCon).filter(
            (addConKey) => !addlCon[addConKey]
        ),
    }
}

export const emptySelectionObject: Record<string, string[]> = {
    categories: [],
    listingLevels: [],
    partnerships: [],
    submissionStatus: [],
    isActive: [],
    displayNameWithFirmName: [],
    sectionNames: [],
    additionalContent: [],
    sectionNameWithProductType: [],
}
export const emptyInputSelectionObject: Record<string, Date | string | null> = {
    lastUpdatedEndDate: null,
    lastUpdatedStartDate: null,
    lastUpdatedInPastDays: null,
}

export const defaultfilterParam = 'df'

export const typeSortOrder = {
    [ListingLevel.PremiumMasterSpec]: 1,
    [ListingLevel.Premium]: 2,
    [ListingLevel.PremiumProprietary]: 3,
    [ListingLevel.Manufacturer]: 4,
} as Record<string, number>
export const sortList = (
    listingData: Partial<ListingMatch>[] = [],
    sortByString = '',
    isPureDate = false
) => {
    let sortBy = sortByString
    const search = new URLSearchParams(window.location.href)
    if (!sortByString && search.has('sortby')) {
        sortBy = decodeURIComponent(search.get('sortby') ?? '')
    }
    const sortKeys = sortBy.split(',')

    const sortedListingData = [...listingData].sort((a, b) => {
        let finalSortIndex = 0
        sortKeys.forEach((sortParam) => {
            const key = sortParam.replace('sectionNumber', 'sectionName')
            if (key.includes('lastUpdated')) {
                //Only taking year and month for sorting as the same are used for grouping
                const a_lastUpdated = a.lastUpdated
                    ?.split('-')
                    .slice(0, 2)
                    .map(Number)
                    .join('-')
                const b_lastUpdated = b.lastUpdated
                    ?.split('-')
                    .slice(0, 2)
                    .map(Number)
                    .join('-')
                if (key.includes('desc'))
                    finalSortIndex =
                        finalSortIndex ||
                        (!isPureDate
                            ? +new Date(b_lastUpdated ?? '') -
                              +new Date(a_lastUpdated ?? '')
                            : +new Date(b.lastUpdated ?? '') -
                              +new Date(a.lastUpdated ?? ''))
                else
                    finalSortIndex =
                        finalSortIndex ||
                        (!isPureDate
                            ? +new Date(a_lastUpdated ?? '') -
                              +new Date(b_lastUpdated ?? '')
                            : +new Date(a.lastUpdated ?? '') -
                              +new Date(b.lastUpdated ?? ''))
            } else if (!key.includes('lastUpdated') && key.includes('desc')) {
                const newKey = key.split(' ')[0] as keyof Partial<ListingMatch>
                const firstArg = a[newKey]?.toString() || ''
                const secondArg = b[newKey]?.toString() || ''
                if (newKey === 'type') {
                    finalSortIndex =
                        finalSortIndex ||
                        typeSortOrder[secondArg] - typeSortOrder[firstArg]
                } else if (newKey === 'categories') {
                    finalSortIndex =
                        finalSortIndex ||
                        (b.categories?.join(',') as string)?.localeCompare(
                            a.categories?.join(',') as string
                        )
                } else
                    finalSortIndex =
                        finalSortIndex || secondArg?.localeCompare(firstArg)
            } else {
                const newKey = key as keyof Partial<ListingMatch>
                const firstArg = a[newKey]?.toString() || ''
                const secondArg = b[newKey]?.toString() || ''
                if (newKey === 'type') {
                    finalSortIndex =
                        finalSortIndex ||
                        typeSortOrder[firstArg] - typeSortOrder[secondArg]
                } else if (newKey === 'categories') {
                    finalSortIndex =
                        finalSortIndex ||
                        (a.categories?.join(',') as string)?.localeCompare(
                            b.categories?.join(',') as string
                        )
                } else
                    finalSortIndex =
                        finalSortIndex || firstArg?.localeCompare(secondArg)
            }
        })
        return finalSortIndex
    })
    return sortedListingData
}

// This is shown when there are no data yet
export const initialListings = [
    {
        listingId: '054281e2-7cfb-43f2-a362-0af879713c8c',
        categories: ['Administration'],
        displayNameWithFirmName:
            "'Deltek Windows & Doors'%|%'Deltek Windows & Doors'",
        firmName: 'Deltek Windows & Doors',
        productName: 'New Product 06082021',
        productType: 'Aluminum Ladders',
        firmLogoUrl:
            'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/b81124ec-5fed-4282-b109-a2110ca3750c/images/profileInfo/KV5bT1MFRJyLv1W45aYz_Deltek_windows_logo.png',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Premium Review',
        lastUpdated: '2021-06-27T05:54:37.758Z',
        isActive: true,
        firmId: 'b81124ec-5fed-4282-b109-a2110ca3750c',
        images: [],
        videos: [],
        catalogs: [
            {
                path:
                    'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/b81124ec-5fed-4282-b109-a2110ca3750c/documents/products/sNTInhQZSeSLfVz0cwgq_Lorem Ipsum - All the facts - Lipsum generator.pdf',
                thumbnailPath: null,
                title: 'Lorem Ipsum - All the facts - Lipsum generator.pdf',
                uploadedDate: '2021-06-07T20:00:37.137Z',
            },
        ],
        manufacturerDocs: [
            {
                docType: 'ShortForm',
                docVariation: 'Unknown',
                expirationDate: '2021-06-07T20:00:36.943Z',
                path:
                    'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/b81124ec-5fed-4282-b109-a2110ca3750c/documents/products/DlaKCCdSIuJeA7waUh0c_Lorem Ipsum - All the facts - Lipsum generator.pdf',
                title: 'Lorem Ipsum - All the facts - Lipsum generator.pdf',
                id: 'bec186b0-fac3-4f57-a18d-469a065bb995',
            },
            {
                docType: 'FullLength',
                docVariation: 'Unknown',
                expirationDate: '2021-06-07T20:00:36.766Z',
                path:
                    'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/b81124ec-5fed-4282-b109-a2110ca3750c/documents/products/W5oTWp2MQfSCzkFsH9Rq_Lorem Ipsum - All the facts - Lipsum generator.pdf',
                title: 'Lorem Ipsum - All the facts - Lipsum generator.pdf',
                id: '653b22df-a5a4-48c3-9248-7e808663379c',
            },
        ],
    },
    {
        listingId: '76482bfd-7c6d-43fd-82f3-15a8c76bab98',
        categories: ['Administration'],
        displayNameWithFirmName:
            "'Meridian Systems, a Trimble Company'%|%'Meridian Systems, a Trimble Company'",
        firmName: 'Meridian Systems, a Trimble Company',
        productName: 'New Product  06082021',
        productType: 'Product Management Software Package',
        firmLogoUrl:
            'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/67882169-ded8-4502-bac0-819b697246a6/images/profileInfo/cV3SxTKPR3qxov26dTr6_Untitled.png',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Premium Review',
        lastUpdated: '2021-06-16T05:56:12.009Z',
        isActive: true,
        firmId: '67882169-ded8-4502-bac0-819b697246a6',
        images: [],
        videos: [],
        manufacturerDocs: [],
    },
    {
        listingId: '9c61e99d-d5bb-4829-b5dc-7a1d12e8a130',
        categories: ['Administration'],
        displayNameWithFirmName:
            "'Meridian Systems, a Trimble Company'%|%'Meridian Systems, a Trimble Company'",
        firmName: 'Meridian Systems, a Trimble Company',
        productName: 'New Product 06082021 Test 3',
        productType: 'Product Management Software Package',
        firmLogoUrl:
            'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/67882169-ded8-4502-bac0-819b697246a6/images/profileInfo/cV3SxTKPR3qxov26dTr6_Untitled.png',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Premium Review',
        lastUpdated: '2021-06-15T22:17:04.103Z',
        isActive: true,
        firmId: '67882169-ded8-4502-bac0-819b697246a6',
        images: [],
        videos: [],
        manufacturerDocs: [],
    },
    {
        listingId: '1501e18a-bc38-41dd-b2af-bd8fd15eab00',
        categories: ['Administration'],
        displayNameWithFirmName:
            "'Meridian Systems, a Trimble Company'%|%'Meridian Systems, a Trimble Company'",
        firmName: 'Meridian Systems, a Trimble Company',
        productName: 'New Product 06082021',
        productType: 'Product Management Software Package',
        firmLogoUrl:
            'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/67882169-ded8-4502-bac0-819b697246a6/images/profileInfo/cV3SxTKPR3qxov26dTr6_Untitled.png',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Premium Review',
        lastUpdated: '2021-06-15T14:58:30.751Z',
        isActive: true,
        firmId: '67882169-ded8-4502-bac0-819b697246a6',
        images: [],
        videos: [],
    },
    {
        listingId: '3520712b-5041-4ad8-b4fa-707aaa20cf75',
        categories: ['Administration'],
        displayNameWithFirmName:
            "'Meridian Systems, a Trimble Company'%|%'Meridian Systems, a Trimble Company'",
        firmName: 'Meridian Systems, a Trimble Company',
        productName: 'New Product 06082021 Test 3 1',
        productType: 'Product Management Software Package',
        firmLogoUrl:
            'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/67882169-ded8-4502-bac0-819b697246a6/images/profileInfo/cV3SxTKPR3qxov26dTr6_Untitled.png',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Premium Review',
        lastUpdated: '2021-06-15T10:47:56.821Z',
        isActive: true,
        firmId: '67882169-ded8-4502-bac0-819b697246a6',
        images: [],
        videos: [],
        manufacturerDocs: [],
    },
    {
        listingId: '8cefd7e8-09ea-4d10-8215-5073e0be64a1',
        categories: ['Administration'],
        displayNameWithFirmName: "'Autodesk Inc.'%|%'Autodesk Inc.'",
        firmName: 'Autodesk Inc.',
        productName: 'QE test 05312021002 - Added Product',
        productType: 'Product Management Software Package',
        firmLogoUrl:
            'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/f6f5d401-ada7-4961-9327-662bfb28d125/images/profileInfo/ut0hOvyCR2mEtaZ1Avqw_logoMain.png',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Completed',
        lastUpdated: '2021-06-11T21:28:10.760Z',
        isActive: true,
        firmId: 'f6f5d401-ada7-4961-9327-662bfb28d125',
        images: [],
        videos: [],
        manufacturerDocs: [],
    },
    {
        listingId: 'bea3799d-d5ad-4171-ad20-6e7e105a32a5',
        categories: ['Administration'],
        displayNameWithFirmName:
            "'Meridian Systems, a Trimble Company'%|%'Meridian Systems, a Trimble Company'",
        firmName: 'Meridian Systems, a Trimble Company',
        productName: 'New Product 06092021',
        productType: 'Product Management Software Package',
        firmLogoUrl:
            'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/67882169-ded8-4502-bac0-819b697246a6/images/profileInfo/cV3SxTKPR3qxov26dTr6_Untitled.png',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Premium MasterSpec Review',
        lastUpdated: '2021-06-10T11:37:32.958Z',
        isActive: true,
        firmId: '67882169-ded8-4502-bac0-819b697246a6',
        images: [
            {
                id: '22703ab9-82e1-46e0-a313-1e64cc50b390',
                filename:
                    'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/67882169-ded8-4502-bac0-819b697246a6/images/products/CI5UZ7IJS6esxMHaPcD0_laptop4.jpg',
                isPrimary: true,
                order: 0,
                originalFilename: 'laptop4.jpg',
            },
        ],
        videos: [],
        catalogs: [
            {
                path:
                    'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/67882169-ded8-4502-bac0-819b697246a6/documents/products/CC1VW0JTRWeMWVm4I7LG_Lorem Ipsum - All the facts - Lipsum generator.pdf',
                title: 'Lorem Ipsum - All the facts - Lipsum generator.pdf',
                uploadedDate: '2021-06-09T19:57:39.149Z',
            },
        ],
        manufacturerDocs: [
            {
                docType: 'ShortForm',
                docVariation: 'Unknown',
                expirationDate: '2021-06-09T19:57:38.854Z',
                path:
                    'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/67882169-ded8-4502-bac0-819b697246a6/documents/products/0kjoKhuvTcu9Fxr41CJ7_Lorem Ipsum - All the facts - Lipsum generator.pdf',
                title: 'Lorem Ipsum - All the facts - Lipsum generator.pdf',
                id: 'cf4abc86-6b87-4baa-9233-885364dbc070',
            },
            {
                docType: 'FullLength',
                docVariation: 'Unknown',
                expirationDate: '2021-06-09T19:57:38.540Z',
                path:
                    'https://kpfileverstorageaccount.blob.core.windows.net/kp-verified/67882169-ded8-4502-bac0-819b697246a6/documents/products/ZUIYEnmRTeuy79UQwqfw_Lorem Ipsum - All the facts - Lipsum generator.pdf',
                title: 'Lorem Ipsum - All the facts - Lipsum generator.pdf',
                id: 'ce8b3157-b2b9-446e-9f12-c6cd4deea7b0',
            },
        ],
    },
    {
        listingId: 'b27280c6-f5f7-42d6-86ca-138f7761b254',
        categories: ['Administration'],
        displayNameWithFirmName: "'Autodesk Inc.'%|%'Autodesk Inc.'",
        firmName: 'Autodesk Inc.',
        productName: 'Product Administration - Newly Added 06072021',
        productType: 'Product Management Software Package',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Completed',
        lastUpdated: '2021-06-07T18:06:47.803Z',
        isActive: true,
        firmId: 'f6f5d401-ada7-4961-9327-662bfb28d125',
        images: [],
        videos: [],
        catalogs: [],
        manufacturerDocs: [],
    },
    {
        listingId: '37b87e3d-5047-4674-8470-b694bc734dd3',
        categories: ['Administration'],
        displayNameWithFirmName:
            "'Viewpoint, Inc.; a Trimble Company'%|%'Viewpoint, Inc.; a Trimble Company'",
        firmName: 'Viewpoint, Inc.; a Trimble Company',
        productName: 'Unknown Product Name',
        productType: 'Product Management Software Package',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Completed',
        lastUpdated: '2019-06-05T13:11:06.000Z',
        isActive: true,
        firmId: '50c03b92-b9a0-4136-b3f2-0921e64ac8d3',
        catalogs: [],
    },
    {
        listingId: '1d21e54f-d81b-4c1a-80d6-68d8c073e736',
        categories: ['Administration'],
        displayNameWithFirmName:
            "'Procore Technologies, Inc.'%|%'Procore Technologies, Inc.'",
        firmName: 'Procore Technologies, Inc.',
        productName: 'Unknown Product Name',
        productType: 'Product Management Software Package',
        sectionName: '013100 - PROJECT MANAGEMENT AND COORDINATION',
        type: 'Manufacturer',
        submissionStatus: 'Completed',
        lastUpdated: '2019-06-05T13:10:49.000Z',
        isActive: true,
        firmId: '46b70ded-f4bc-432f-8411-85ca65becfde',
        catalogs: [],
    },
] as Partial<ListingMatch>[]
